import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  DollarSign, 
  TrendingUp, 
  Clock, 
  AlertCircle,
  CheckCircle2,
  XCircle,
  FileText,
  Search,
  Download,
  RefreshCw,
  MoreHorizontal,
  Send,
  Plus
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import BillingSidebar from "@/components/layout/BillingSidebar";
import BillingHeader from "@/components/layout/BillingHeader";
import ClaimDetailDialog from "@/components/billing/ClaimDetailDialog";
import { ClaimSubmissionForm, type ClaimFormData } from "@/components/billing/ClaimSubmissionForm";
import { supabase } from "@/integrations/supabase/client";
import { format, differenceInDays } from "date-fns";
import { useToast } from "@/hooks/use-toast";

// CPT code pricing reference
const cptPricing: Record<string, number> = {
  "99201": 75,
  "99202": 95,
  "99203": 125,
  "99211": 45,
  "99212": 85,
  "99213": 125,
  "99214": 150,
  "99215": 200,
  "90837": 175,
  "90834": 125,
  "90832": 85,
  "99457": 50,
  "99458": 40,
  "99453": 20,
  "99454": 65,
};

interface ClaimData {
  id: string;
  member_id: string;
  member_name: string;
  scheduled_date: string;
  visit_type: string;
  cpt_code: string | null;
  billing_status: string | null;
  billing_units: number | null;
  actual_duration_minutes: number | null;
  insurance_type: string;
  amount: number;
}

export default function BillingDashboard() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedClaim, setSelectedClaim] = useState<ClaimData | null>(null);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [createClaimOpen, setCreateClaimOpen] = useState(false);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch claims data from telehealth sessions
  const { data: claims, isLoading, refetch } = useQuery({
    queryKey: ["billing-claims"],
    queryFn: async () => {
      // Get telehealth sessions with billing data
      const { data: sessions, error: sessionsError } = await supabase
        .from("telehealth_sessions")
        .select(`
          *,
          member_profile:profiles!telehealth_sessions_member_id_profiles_fkey(id, display_name)
        `)
        .in("status", ["completed", "in_progress", "scheduled", "waiting"])
        .order("scheduled_date", { ascending: false });

      if (sessionsError) throw sessionsError;
      if (!sessions || sessions.length === 0) return [];

      // Fetch intake forms for bounded member set
      const memberIds = [...new Set(sessions.map((s: any) => s.member_id))];
      const { data: intakeForms } = await supabase
        .from("patients")
        .select("user_id, first_name, last_name, insurance_type")
        .in("user_id", memberIds);

      const claimsData: ClaimData[] = sessions.map((session: any) => {
        const intake = intakeForms?.find(i => i.user_id === session.member_id);
        const profile = session.member_profile;
        const cptCode = session.cpt_code || getCptCodeFromVisitType(session.visit_type);
        const amount = cptPricing[cptCode] || 100;

        return {
          id: session.id,
          member_id: session.member_id,
          member_name: intake 
            ? `${intake.first_name} ${intake.last_name}` 
            : profile?.display_name || "Unknown Patient",
          scheduled_date: session.scheduled_date,
          visit_type: session.visit_type,
          cpt_code: cptCode,
          billing_status: session.billing_status || (session.status === "completed" ? "pending" : "unbilled"),
          billing_units: session.billing_units || 1,
          actual_duration_minutes: session.actual_duration_minutes,
          insurance_type: intake?.insurance_type || "medicare",
          amount: amount * (session.billing_units || 1),
        };
      });

      return claimsData;
    },
  });

  // Mutation for creating new claims
  const createClaimMutation = useMutation({
    mutationFn: async (data: ClaimFormData) => {
      // Create a telehealth session record for the new claim
      const { data: session, error } = await supabase
        .from("telehealth_sessions")
        .insert({
          member_id: data.patientId,
          provider_id: data.patientId, // Using member ID as placeholder
          scheduled_date: new Date().toISOString().split("T")[0],
          scheduled_time: new Date().toTimeString().split(" ")[0].substring(0, 5),
          visit_type: data.serviceType.toLowerCase().replace(" ", "_"),
          cpt_code: data.cptCodes[0]?.code || "99213",
          billing_status: "submitted",
          billing_units: data.cptCodes.length,
          status: "completed",
          notes: data.notes,
        })
        .select()
        .single();

      if (error) throw error;
      return session;
    },
    onSuccess: () => {
      toast({
        title: "Claim Created",
        description: "New claim has been submitted successfully.",
      });
      setCreateClaimOpen(false);
      queryClient.invalidateQueries({ queryKey: ["billing-claims"] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Helper function to get CPT code from visit type
  function getCptCodeFromVisitType(visitType: string): string {
    switch (visitType) {
      case "general": return "99213";
      case "followup": return "99212";
      case "urgent": return "99214";
      case "mental_health": return "90837";
      case "rpm": return "99457";
      default: return "99213";
    }
  }

  // Calculate metrics from real data
  // billing_status values: pending, submitted, approved, denied
  const metrics = {
    totalRevenue: claims?.filter(c => c.billing_status === "approved")
      .reduce((sum, c) => sum + c.amount, 0) || 0,
    totalSubmitted: claims?.filter(c => ["submitted", "pending", "approved", "denied"].includes(c.billing_status || ""))
      .length || 0,
    claimsPending: claims?.filter(c => c.billing_status === "pending" || c.billing_status === "submitted")
      .length || 0,
    claimsPaid: claims?.filter(c => c.billing_status === "approved").length || 0,
    claimsDenied: claims?.filter(c => c.billing_status === "denied").length || 0,
    claimsUnbilled: claims?.filter(c => c.billing_status === "unbilled" || !c.billing_status)
      .length || 0,
    pendingAmount: claims?.filter(c => c.billing_status === "pending" || c.billing_status === "submitted")
      .reduce((sum, c) => sum + c.amount, 0) || 0,
  };

  const collectionRate = metrics.totalSubmitted > 0 
    ? ((metrics.claimsPaid / metrics.totalSubmitted) * 100).toFixed(1) 
    : "0";
  
  const denialRate = metrics.totalSubmitted > 0 
    ? ((metrics.claimsDenied / metrics.totalSubmitted) * 100).toFixed(1) 
    : "0";

  // Filter claims
  const filteredClaims = claims?.filter(claim => {
    const matchesSearch = claim.member_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      claim.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || claim.billing_status === statusFilter;
    return matchesSearch && matchesStatus;
  }) || [];

  // A/R Aging calculation
  const today = new Date();
  const agingBuckets = [
    { range: "0-30 days", claims: [] as ClaimData[] },
    { range: "31-60 days", claims: [] as ClaimData[] },
    { range: "61-90 days", claims: [] as ClaimData[] },
    { range: "91-120 days", claims: [] as ClaimData[] },
    { range: "120+ days", claims: [] as ClaimData[] },
  ];

  claims?.filter(c => c.billing_status === "pending" || c.billing_status === "submitted")
    .forEach(claim => {
      const daysSince = differenceInDays(today, new Date(claim.scheduled_date));
      if (daysSince <= 30) agingBuckets[0].claims.push(claim);
      else if (daysSince <= 60) agingBuckets[1].claims.push(claim);
      else if (daysSince <= 90) agingBuckets[2].claims.push(claim);
      else if (daysSince <= 120) agingBuckets[3].claims.push(claim);
      else agingBuckets[4].claims.push(claim);
    });

  const totalAging = agingBuckets.reduce((sum, b) => sum + b.claims.reduce((s, c) => s + c.amount, 0), 0);

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1" />Paid</Badge>;
      case "pending":
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case "denied":
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><XCircle className="h-3 w-3 mr-1" />Denied</Badge>;
      case "submitted":
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100"><FileText className="h-3 w-3 mr-1" />Submitted</Badge>;
      case "unbilled":
        return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Unbilled</Badge>;
      default:
        return <Badge variant="outline">{status || "Unbilled"}</Badge>;
    }
  };

  const getInsuranceBadge = (type: string) => {
    const colors: Record<string, string> = {
      medicare: "bg-blue-100 text-blue-700",
      medicaid: "bg-purple-100 text-purple-700",
      dual_eligible: "bg-indigo-100 text-indigo-700",
    };
    return (
      <Badge className={`${colors[type] || "bg-muted"} capitalize`}>
        {type.replace("_", " ")}
      </Badge>
    );
  };

  return (
    <div className="flex min-h-screen bg-background">
      <BillingSidebar />
      <div className="flex-1 flex flex-col">
        <BillingHeader />
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Revenue Cycle Dashboard</h1>
                <p className="text-muted-foreground">Monitor claims, collections, and financial performance</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Dialog open={createClaimOpen} onOpenChange={setCreateClaimOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Claim
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Create New Claim</DialogTitle>
                      <DialogDescription>
                        Submit a new claim for billing and reimbursement.
                      </DialogDescription>
                    </DialogHeader>
                    <ClaimSubmissionForm
                      onSubmit={(data) => createClaimMutation.mutate(data)}
                      onCancel={() => setCreateClaimOpen(false)}
                    />
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Revenue (Paid)</p>
                      <p className="text-2xl font-bold">${metrics.totalRevenue.toLocaleString()}</p>
                      <div className="flex items-center text-emerald-600 text-sm mt-1">
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        <span>{metrics.claimsPaid} claims paid</span>
                      </div>
                    </div>
                    <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <DollarSign className="h-6 w-6 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Pending Amount</p>
                      <p className="text-2xl font-bold">${metrics.pendingAmount.toLocaleString()}</p>
                      <div className="flex items-center text-amber-600 text-sm mt-1">
                        <Clock className="h-4 w-4 mr-1" />
                        <span>{metrics.claimsPending} claims pending</span>
                      </div>
                    </div>
                    <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <Clock className="h-6 w-6 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Collection Rate</p>
                      <p className="text-2xl font-bold">{collectionRate}%</p>
                      <div className="flex items-center text-blue-600 text-sm mt-1">
                        <TrendingUp className="h-4 w-4 mr-1" />
                        <span>{metrics.totalSubmitted} total submitted</span>
                      </div>
                    </div>
                    <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Denial Rate</p>
                      <p className="text-2xl font-bold">{denialRate}%</p>
                      <div className="flex items-center text-red-600 text-sm mt-1">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        <span>{metrics.claimsDenied} denied</span>
                      </div>
                    </div>
                    <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                      <AlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Claims Pipeline & A/R Aging */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Claims Pipeline */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Claims Pipeline</CardTitle>
                  <CardDescription>Current status of all claims</CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-5 gap-4">
                        <div className="text-center p-4 bg-muted/50 rounded-lg">
                          <p className="text-3xl font-bold text-muted-foreground">{metrics.claimsUnbilled}</p>
                          <p className="text-sm text-muted-foreground">Unbilled</p>
                        </div>
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                          <p className="text-3xl font-bold text-blue-600">{metrics.totalSubmitted}</p>
                          <p className="text-sm text-muted-foreground">Submitted</p>
                        </div>
                        <div className="text-center p-4 bg-amber-50 rounded-lg">
                          <p className="text-3xl font-bold text-amber-600">{metrics.claimsPending}</p>
                          <p className="text-sm text-muted-foreground">Pending</p>
                        </div>
                        <div className="text-center p-4 bg-emerald-50 rounded-lg">
                          <p className="text-3xl font-bold text-emerald-600">{metrics.claimsPaid}</p>
                          <p className="text-sm text-muted-foreground">Paid</p>
                        </div>
                        <div className="text-center p-4 bg-red-50 rounded-lg">
                          <p className="text-3xl font-bold text-red-600">{metrics.claimsDenied}</p>
                          <p className="text-sm text-muted-foreground">Denied</p>
                        </div>
                      </div>
                      <div className="mt-6 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>First Pass Rate</span>
                          <span className="font-medium">
                            {metrics.totalSubmitted > 0 
                              ? (((metrics.claimsPaid + metrics.claimsPending) / metrics.totalSubmitted) * 100).toFixed(1)
                              : 0}%
                          </span>
                        </div>
                        <Progress 
                          value={metrics.totalSubmitted > 0 
                            ? ((metrics.claimsPaid + metrics.claimsPending) / metrics.totalSubmitted) * 100
                            : 0} 
                          className="h-2" 
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Unbilled Sessions Alert */}
              <Card>
                <CardHeader>
                  <CardTitle>Action Required</CardTitle>
                  <CardDescription>Sessions needing attention</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {metrics.claimsUnbilled > 0 && (
                      <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-5 w-5 text-amber-600" />
                          <div>
                            <p className="font-medium text-amber-800">Unbilled Sessions</p>
                            <p className="text-sm text-amber-600">{metrics.claimsUnbilled} sessions to bill</p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline">Bill Now</Button>
                      </div>
                    )}
                    {metrics.claimsDenied > 0 && (
                      <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center gap-2">
                          <XCircle className="h-5 w-5 text-red-600" />
                          <div>
                            <p className="font-medium text-red-800">Denied Claims</p>
                            <p className="text-sm text-red-600">{metrics.claimsDenied} need review</p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline">Review</Button>
                      </div>
                    )}
                    {metrics.claimsUnbilled === 0 && metrics.claimsDenied === 0 && (
                      <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        <p className="text-emerald-800">All caught up!</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* A/R Aging */}
            <Card>
              <CardHeader>
                <CardTitle>Accounts Receivable Aging</CardTitle>
                <CardDescription>Outstanding balances by age</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-4">
                  {agingBuckets.map((bucket, index) => {
                    const amount = bucket.claims.reduce((sum, c) => sum + c.amount, 0);
                    const percentage = totalAging > 0 ? (amount / totalAging) * 100 : 0;
                    return (
                      <div key={index} className="text-center p-4 border rounded-lg">
                        <p className="text-sm font-medium text-muted-foreground">{bucket.range}</p>
                        <p className="text-xl font-bold mt-2">${amount.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">{bucket.claims.length} claims</p>
                        <div className="mt-2">
                          <Progress value={percentage} className="h-1.5" />
                          <p className="text-xs text-muted-foreground mt-1">{percentage.toFixed(0)}%</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Claims Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Claims</CardTitle>
                    <CardDescription>All telehealth session claims</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search claims..."
                        className="pl-9 w-64"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="submitted">Submitted</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="denied">Denied</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  </div>
                ) : filteredClaims.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No claims found</p>
                    <p className="text-sm">Claims are generated from completed telehealth sessions</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Patient</TableHead>
                        <TableHead>Service Date</TableHead>
                        <TableHead>Visit Type</TableHead>
                        <TableHead>CPT</TableHead>
                        <TableHead>Payer</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredClaims.map((claim) => (
                        <TableRow 
                          key={claim.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => {
                            setSelectedClaim(claim);
                            setClaimDialogOpen(true);
                          }}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                  {claim.member_name.split(" ").map(n => n[0]).join("")}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium">{claim.member_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>{format(new Date(claim.scheduled_date), "MMM d, yyyy")}</TableCell>
                          <TableCell className="capitalize">{claim.visit_type.replace("_", " ")}</TableCell>
                          <TableCell className="font-mono">{claim.cpt_code}</TableCell>
                          <TableCell>{getInsuranceBadge(claim.insurance_type)}</TableCell>
                          <TableCell className="font-medium">${claim.amount}</TableCell>
                          <TableCell>{getStatusBadge(claim.billing_status)}</TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedClaim(claim);
                                  setClaimDialogOpen(true);
                                }}>
                                  <FileText className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                {claim.billing_status === "pending" && (
                                  <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedClaim(claim);
                                    setClaimDialogOpen(true);
                                  }}>
                                    <Send className="h-4 w-4 mr-2" />
                                    Submit Claim
                                  </DropdownMenuItem>
                                )}
                                {claim.billing_status === "submitted" && (
                                  <>
                                    <DropdownMenuItem 
                                      className="text-emerald-600"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedClaim(claim);
                                        setClaimDialogOpen(true);
                                      }}
                                    >
                                      <CheckCircle2 className="h-4 w-4 mr-2" />
                                      Mark as Paid
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      className="text-red-600"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedClaim(claim);
                                        setClaimDialogOpen(true);
                                      }}
                                    >
                                      <XCircle className="h-4 w-4 mr-2" />
                                      Mark as Denied
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {claim.billing_status === "denied" && (
                                  <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedClaim(claim);
                                    setClaimDialogOpen(true);
                                  }}>
                                    <Send className="h-4 w-4 mr-2" />
                                    Resubmit Claim
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      {/* Claim Detail Dialog */}
      <ClaimDetailDialog
        claim={selectedClaim}
        open={claimDialogOpen}
        onOpenChange={setClaimDialogOpen}
      />
    </div>
  );
}
